import HorizontalBarChart from '../../../components/HorizontalBarChart';
import { formatMoney, formatPct, findPaygPlatformCrossoverMonth } from '../model/engine';
import { PLAID_GROWTH_PLATFORM_FEE } from '../model/seedDefaults';
import SensitivityPanel from './SensitivityPanel';

export default function ResultsPanel({ projection, profiles, scenario, onPlaidModeChange }) {
  const { months, mc, runs, distribution } = projection;
  const last = months[months.length - 1];
  const first = months[0];
  const plaidMode = scenario?.plaidMode || 'payg';

  const chartItems = months.map((m) => [
    m.label,
    m.planRevenueP50 ?? m.planRevenue ?? m.revenueP50 ?? m.revenue,
  ]);
  const cogsItems = months.map((m) => [m.label, m.plaidCogsP50 ?? m.plaidCogs]);
  const profitItems = months.map((m) => [m.label, m.profitP50 ?? m.profit]);

  const crossover = findPaygPlatformCrossoverMonth(months);
  const crossoverNote = crossover
    ? `Modeled PAYG COGS crosses the Growth platform fee ($${PLAID_GROWTH_PLATFORM_FEE.toLocaleString()}/mo) in ${crossover.label} — consider Growth platform when headcount scales.`
    : `Modeled PAYG COGS stays below the Growth platform fee ($${PLAID_GROWTH_PLATFORM_FEE.toLocaleString()}/mo) through the horizon — stay PAYG.`;

  return (
    <div className="economics-results">
      <section className="chart-card economics-panel">
        <h2>Plaid billing mode</h2>
        <p className="chart-hint">
          PAYG uses modeled per-Item COGS. Growth platform applies a fixed $
          {PLAID_GROWTH_PLATFORM_FEE.toLocaleString()}/mo fee (replaces PAYG in profit math).
        </p>
        <div className="economics-preset-row">
          <button
            type="button"
            className={plaidMode === 'payg' ? 'primary-button' : 'secondary-button'}
            onClick={() => onPlaidModeChange?.('payg')}
          >
            Pay As You Go
          </button>
          <button
            type="button"
            className={plaidMode === 'growth_platform' ? 'primary-button' : 'secondary-button'}
            onClick={() => onPlaidModeChange?.('growth_platform')}
          >
            Growth platform ($2k/mo)
          </button>
        </div>
        <p className="module-status economics-callout">{crossoverNote}</p>
      </section>

      {mc && (
        <p className="module-status economics-callout">
          Monte Carlo · {runs} runs · {distribution} distribution · table/chart show{' '}
          <strong>P50</strong> (P10–P90 in table when MC)
        </p>
      )}

      <div className="kpi-grid">
        <article className="kpi-card">
          <p className="kpi-label">Paying linkers (now)</p>
          <p className="kpi-value">{first?.payingP50 ?? first?.paying ?? '—'}</p>
          <p className="kpi-hint">
            {mc && first?.payingP10 != null
              ? `P10–P90: ${first.payingP10}–${first.payingP90}`
              : 'Month 0 headcount'}
          </p>
        </article>
        <article className="kpi-card">
          <p className="kpi-label">MRR (now)</p>
          <p className="kpi-value">
            {formatMoney(
              first?.planRevenueP50 ??
                first?.planRevenue ??
                first?.revenueP50 ??
                first?.revenue
            )}
          </p>
          <p className="kpi-hint">Modeled subscription MRR (P50)</p>
        </article>
        <article className="kpi-card">
          <p className="kpi-label">Plaid COGS (now)</p>
          <p className="kpi-value">{formatMoney(first?.plaidCogsP50 ?? first?.plaidCogs)}</p>
          <p className="kpi-hint">
            {plaidMode === 'growth_platform'
              ? 'Growth platform fee (effective)'
              : 'Portfolio Plaid spend (PAYG)'}
            {first?.plaidCogsPayg != null &&
              plaidMode === 'growth_platform' &&
              ` · PAYG would be ${formatMoney(first.plaidCogsPayg)}`}
          </p>
        </article>
        <article className="kpi-card">
          <p className="kpi-label">Contribution profit (horizon)</p>
          <p className="kpi-value">{formatMoney(last?.profitP50 ?? last?.profit)}</p>
          <p className="kpi-hint">
            M{months.length - 1} · after Plaid, Stripe, AI, and Auth · excludes fixed/marketing costs
            {mc && last?.profitP10 != null
              ? ` · P10–P90 ${formatMoney(last.profitP10)}–${formatMoney(last.profitP90)}`
              : ` · ${formatPct(last?.profitPct)} margin`}
          </p>
        </article>
      </div>

      <div className="product-grid">
        <section className="chart-card">
          <h2>MRR projection</h2>
          <p className="chart-hint">Modeled subscription MRR by month {mc ? '(P50)' : ''}</p>
          <HorizontalBarChart items={chartItems} emptyMessage="No projection data." />
        </section>
        <section className="chart-card">
          <h2>Plaid COGS projection</h2>
          <p className="chart-hint">
            Effective COGS {plaidMode === 'growth_platform' ? '(Growth platform)' : '(PAYG)'} vs $
            {PLAID_GROWTH_PLATFORM_FEE.toLocaleString()} reference
          </p>
          <HorizontalBarChart items={cogsItems} emptyMessage="No projection data." />
          <p className="chart-hint">
            Reference: Growth platform {formatMoney(PLAID_GROWTH_PLATFORM_FEE)}/mo
          </p>
        </section>
        <section className="chart-card">
          <h2>Contribution profit projection</h2>
          <p className="chart-hint">
            Revenue − Plaid − Stripe − AI − optional Auth once; excludes fixed/marketing costs{' '}
            {mc ? '(P50)' : ''}
          </p>
          <HorizontalBarChart
            items={profitItems}
            emptyMessage="No projection data."
            allowNegative
          />
        </section>
      </div>

      {scenario && <SensitivityPanel scenario={scenario} />}

      <section className="chart-card">
        <h2>Monthly projection table</h2>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Paying</th>
                <th>MRR</th>
                <th>Total revenue</th>
                <th>Plaid COGS</th>
                <th>Auth once</th>
                {plaidMode === 'growth_platform' && <th>PAYG modeled</th>}
                <th>Stripe est.</th>
                <th>AI packs</th>
                <th>AI COGS</th>
                <th>Contribution profit</th>
                <th>Pre-Stripe contribution margin %</th>
                {mc && (
                  <>
                    <th>Rev P10–P90</th>
                    <th>Profit P10–P90</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {months.map((m) => (
                <tr key={m.label}>
                  <td>{m.label}</td>
                  <td>{m.payingP50 ?? m.paying}</td>
                  <td>
                    {formatMoney(
                      m.planRevenueP50 ?? m.planRevenue ?? m.revenueP50 ?? m.revenue
                    )}
                  </td>
                  <td>{formatMoney(m.revenueP50 ?? m.revenue)}</td>
                  <td>{formatMoney(m.plaidCogsP50 ?? m.plaidCogs)}</td>
                  <td>{formatMoney(m.authOnceCogs ?? 0)}</td>
                  {plaidMode === 'growth_platform' && (
                    <td className="economics-muted">
                      {formatMoney(m.plaidCogsPayg ?? m.plaidCogs)}
                    </td>
                  )}
                  <td>{formatMoney(m.stripeFees)}</td>
                  <td>{formatMoney(m.aiPackRevenue ?? 0)}</td>
                  <td>{formatMoney(m.aiCogs ?? 0)}</td>
                  <td>{formatMoney(m.profitP50 ?? m.profit)}</td>
                  <td>{formatPct(m.marginPct)}</td>
                  {mc && (
                    <>
                      <td className="economics-muted">
                        {formatMoney(m.revenueP10)} – {formatMoney(m.revenueP90)}
                      </td>
                      <td className="economics-muted">
                        {formatMoney(m.profitP10)} – {formatMoney(m.profitP90)}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="chart-card">
        <h2>Profile P&L reference</h2>
        <p className="chart-hint">{profiles.length} profiles in active scenario</p>
        <p className="module-footnote">
          Edit profiles in the Profiles tab.{' '}
          {scenario?.growth?.growthMode === 'phased'
            ? 'Global phases compound the paying pool; profile rows shift mix.'
            : 'Each profile compounds its own headcount; different rates change total growth, mix, revenue, and costs.'}
        </p>
      </section>
    </div>
  );
}
